class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Store bound function references for cleanup
    this.boundClose = this.close.bind(this);
    this.boundKeyupHandler = (evt) => evt.code === 'Escape' && this.close();
    this.boundCartLinkClick = null;
    this.boundCartLinkKeydown = null;
    this.boundOverlayClick = null;

    this.addEventListener('keyup', this.boundKeyupHandler);
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      this.boundOverlayClick = this.boundClose;
      overlay.addEventListener('click', this.boundOverlayClick);
    }
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    // Remove old listeners if they exist
    if (this.boundCartLinkClick) {
      cartLink.removeEventListener('click', this.boundCartLinkClick);
    }
    if (this.boundCartLinkKeydown) {
      cartLink.removeEventListener('keydown', this.boundCartLinkKeydown);
    }

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    // Store bound functions
    this.boundCartLinkClick = (event) => {
      event.preventDefault();
      this.open(cartLink);
    };
    this.boundCartLinkKeydown = (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    };

    cartLink.addEventListener('click', this.boundCartLinkClick);
    cartLink.addEventListener('keydown', this.boundCartLinkKeydown);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  disconnectedCallback() {
    // Cleanup: Remove all event listeners when element is removed
    this.removeEventListener('keyup', this.boundKeyupHandler);

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay && this.boundOverlayClick) {
      overlay.removeEventListener('click', this.boundOverlayClick);
    }

    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      if (this.boundCartLinkClick) {
        cartLink.removeEventListener('click', this.boundCartLinkClick);
      }
      if (this.boundCartLinkKeydown) {
        cartLink.removeEventListener('keydown', this.boundCartLinkKeydown);
      }
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    // Remove old listeners if they exist
    if (this.boundSummaryClick) {
      cartDrawerNote.removeEventListener('click', this.boundSummaryClick);
    }
    if (this.boundSummaryKeyup && cartDrawerNote.parentElement) {
      cartDrawerNote.parentElement.removeEventListener('keyup', this.boundSummaryKeyup);
    }

    // Store bound functions
    this.boundSummaryClick = (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    };
    this.boundSummaryKeyup = onKeyUpEscape;

    cartDrawerNote.addEventListener('click', this.boundSummaryClick);
    if (cartDrawerNote.parentElement) {
      cartDrawerNote.parentElement.addEventListener('keyup', this.boundSummaryKeyup);
    }
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      // CRITICAL FIX: Remove old overlay listener before adding new one
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        if (this.boundOverlayClick) {
          overlay.removeEventListener('click', this.boundOverlayClick);
        }
        // Reuse the same bound function reference
        this.boundOverlayClick = this.boundClose;
        overlay.addEventListener('click', this.boundOverlayClick);
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

// Guard to prevent redefinition on hot reload
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// Define CartDrawerItems only when CartItems is available
// (cart.js may load after cart-drawer.js due to defer attribute)
function defineCartDrawerItems() {
  if (typeof CartItems === 'undefined') {
    // CartItems not loaded yet, retry after a short delay
    setTimeout(defineCartDrawerItems, 50);
    return;
  }

  if (customElements.get('cart-drawer-items')) {
    // Already defined
    return;
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

defineCartDrawerItems();
